import { Injectable, inject } from '@angular/core';
import { GeoBrowserService, InteractionService, MapColors } from '@next/arable-lib/geo';
import { SelectedCustomer } from '@next/core-lib/user';
import { select, Store } from '@ngrx/store';
import { EventsKey } from 'ol/events';
import { always } from 'ol/events/condition';
import { FeatureLike } from 'ol/Feature';
import { GeoJSON } from 'ol/format';
import { Select } from 'ol/interaction';
import { Vector as Layer } from 'ol/layer';
import { unByKey } from 'ol/Observable';
import { Vector } from 'ol/source';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import { combineLatest, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { getFilteredCropFieldsIds } from 'src/app/registration/store/selectors/crop-field.selectors';
import { SiteState } from 'src/app/shared/models';
import { hasSearch } from 'src/app/store/selectors';

import { InteractionType } from '../../models';
import {
  InteractionFeatureDeselected,
  InteractionFeatureSelected,
} from '../../store/actions/feature.actions';
import {
  getAllFeatureIds,
  getFeatures,
  getSelectedFeaturesIds,
} from '../../store/selectors/feature.selectors';
import {
  getCropFieldsEnabled,
  getInteractionType,
} from '../../store/selectors/map-context.selectors';

@Injectable({
  providedIn: 'root',
})
export class GeoSelectCropFieldService implements InteractionService {
  private readonly store = inject<Store<SiteState>>(Store);

  interactionTypes = [InteractionType.linkSoilAnalysisToCropField, InteractionType.selectCropField];
  private selectEventsKey: EventsKey;
  private subscription: Subscription;
  private rvoEnabledSubscription = new Subscription();
  private reader = new GeoJSON({});
  private lastCustomer: SelectedCustomer;
  private isSeaching = false;
  private filteredIds: number[] = [];
  private selectRvoEnabled = false;

  readonly source = new Vector({
    wrapX: false,
  });

  readonly style = new Style({
    fill: new Fill({
      color: MapColors.viewFill,
    }),
    stroke: new Stroke({
      color: MapColors.viewStroke,
      width: 1.5,
    }),
  });

  readonly defilteredStyle = new Style({
    fill: new Fill({
      color: MapColors.defilteredViewFill,
    }),
  });

  readonly layer = new Layer({
    source: this.source,
    style: (feature) => this.getStyle(feature),
    zIndex: 2,
  });

  readonly select = new Select({
    toggleCondition: always,
    multi: true,
    layers: (layer) => layer === this.layer,
    style: new Style({
      stroke: new Stroke({
        color: MapColors.selectStroke,
        width: 2.5,
      }),
      fill: new Fill({
        color: MapColors.selectFill,
      }),
    }),
  });

  readonly selectFeatures = this.select.getFeatures();

  getStyle(feature: FeatureLike) {
    if (this.selectRvoEnabled) {
      return this.defilteredStyle;
    }
    return !this.isSeaching || this.filteredIds.indexOf(Number(feature.getId())) !== -1
      ? this.style
      : this.defilteredStyle;
  }

  init(geoService: GeoBrowserService) {
    this.select.setActive(false);
    geoService.map.addLayer(this.layer);
    geoService.map.addInteraction(this.select);

    this.store.pipe(select(getCropFieldsEnabled)).subscribe((enabled) => {
      this.layer.setVisible(enabled);
    });

    this.store.pipe(select(getFeatures)).subscribe((features) => {
      this.source.clear();
      features.forEach((f) => {
        const feature = this.reader.readFeature(f);
        feature.setId(f.id);
        this.source.addFeature(feature);
      });
    });

    combineLatest([
      this.store.pipe(select(hasSearch)),
      this.store.pipe(select(getFilteredCropFieldsIds)),
    ]).subscribe(([isSeaching, ids]: [boolean, number[]]) => {
      this.isSeaching = isSeaching;
      this.filteredIds = ids;
      this.layer.changed();
    });

    combineLatest([
      this.store.pipe(select(getSelectedFeaturesIds)),
      this.store.pipe(select(getAllFeatureIds)),
    ])
      .pipe(
        map(([selectedIds, ids]) => {
          const unselectedIds = ids.filter((id) => selectedIds.indexOf(id) === -1);
          const selectedFeatures = selectedIds.map((id) => this.source.getFeatureById(id));
          const unselectedFeatures = unselectedIds.map((id) => this.source.getFeatureById(id));
          return [selectedFeatures, unselectedFeatures];
        }),
      )
      .subscribe(([selectedFeatures, unselectedFeatures]) => {
        selectedFeatures
          .filter(
            (feature) => this.selectFeatures.getArray().indexOf(feature) === -1 && feature !== null,
          )
          .forEach((feature) => this.selectFeatures.push(feature));
        unselectedFeatures
          .filter((feature) => this.selectFeatures.getArray().indexOf(feature) !== -1)
          .forEach((feature) => {
            this.selectFeatures.remove(feature);
          });
      });

    this.rvoEnabledSubscription.add(
      this.store.pipe(select(getInteractionType)).subscribe((interactionType) => {
        switch (interactionType) {
          case InteractionType.cloneRvo:
            this.selectRvoEnabled = true;
            break;
          default:
            this.selectRvoEnabled = false;
        }
        this.layer.changed();
      }),
    );
  }

  destroy(geoService: GeoBrowserService) {
    geoService.map.removeLayer(this.layer);
    geoService.map.removeInteraction(this.select);
    if (this.rvoEnabledSubscription) {
      this.rvoEnabledSubscription.unsubscribe();
    }
  }

  start() {
    this.subscription = new Subscription();

    this.selectEventsKey = this.select.on('select', (selectEvent) => {
      selectEvent.selected
        .filter((feature) => !!feature && !!feature.getId())
        .forEach((feature) => {
          this.store.dispatch(
            new InteractionFeatureSelected(parseInt(feature.getId() as string, 10)),
          );
        });
      selectEvent.deselected
        .filter((feature) => !!feature && !!feature.getId())
        .forEach((feature) => {
          this.store.dispatch(
            new InteractionFeatureDeselected(parseInt(feature.getId() as string, 10)),
          );
        });
    });

    this.select.setActive(true);
  }

  stop(_: GeoBrowserService) {
    unByKey(this.selectEventsKey);
    this.select.setActive(false);
    this.select.getFeatures().clear();
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  hasCustomerChanged(customer: SelectedCustomer) {
    return !(this.lastCustomer && customer) || customer.id !== this.lastCustomer.id;
  }
}
